import React, { useCallback, useEffect, useMemo } from 'react';
import { range } from 'ramda';
import { ButtonBase as MuiButtonBase, styled } from '@mui/material';
import EDTypography from '../../Typography/EDTypography';
import { incDay, getDate, diffDay2 } from '../../../Utils/dateUtils';
import {
  IBooking,
  ICalendarViewProps,
  dateCaption,
  prepareViewBookings,
} from '../Utils/EDCalendarViewUtils';
import { dateToStr } from '../../../Utils/stringUtils';
import { getDayNameText, isRedDay } from '../../../Utils/dayNames';
import { firstUpper } from '../../../Utils/ramdaCookbook';
import { EDCalendarViewDayViewBooking } from './EDCalendarViewDayViewBooking';
import EDList from '../../List/EDList';
import EDListItem from '../../List/Components/EDListItem';
import classNames from 'classnames';
import EDBox from '../../EDBox/EDBox';
import { ViewDbSettingsContext } from '../../../Features/Delsystem/Bokning/Rutiner/Kalender/Components/SchedulerComponent/ViewDbSettingsContext';

type DayHeaderItem = {
  type: 'header';
  date: Date;
  redDay: boolean;
  today: boolean;
};
type BookingItem = { type: 'booking'; booking: IBooking; isChild: boolean };
type ListItem = DayHeaderItem | BookingItem;

const LOOKBACK_DAYS = 30;

export interface ICalendarViewListViewExtraProps {
  onScrolledDateChange?: (date: Date) => void;
}

export const EDCalendarViewListView: React.FC<
  ICalendarViewProps & ICalendarViewListViewExtraProps
> = ({ listViewRef, ...props }) => {
  const { scrollToCurrentIndexRef: scrollToCurrentIndexRef } = React.useContext(
    ViewDbSettingsContext
  );
  const dayCount = props.dayCount ?? 1;

  const viewStartTime = useMemo(
    () => incDay(getDate(props.currentDate), -LOOKBACK_DAYS),
    [props.currentDate]
  );
  const viewEndTime = useMemo(
    () => incDay(getDate(props.currentDate), dayCount),
    [props.currentDate, dayCount]
  );

  const bookingDays = useMemo(
    () =>
      prepareViewBookings(
        props.bookings,
        viewStartTime,
        viewEndTime,
        booking => dateToStr(booking.startTime),
        false
      ),
    [props.bookings, viewStartTime, viewEndTime]
  );

  const flatItems = useMemo(() => {
    const items: ListItem[] = [];
    range(0, LOOKBACK_DAYS + dayCount).forEach(day => {
      const d = incDay(viewStartTime, day);
      const key = dateToStr(d);
      const bookings: IBooking[] = bookingDays[key];

      if (bookings && bookings.length > 0) {
        items.push({
          type: 'header',
          date: d,
          redDay: isRedDay(d),
          today: diffDay2(d, getDate()) === 0,
        });

        // Gruppera bokningar som har fältet Huvudakt under bokningen med samma
        // Bokningsnummer, och indenterar dem relativt normala listobjekt.
        for (const booking of bookings) {
          if (!booking.Huvudakt) {
            items.push({ type: 'booking', booking, isChild: false });
            const children = bookings.filter(
              b => b.Huvudakt && b.Huvudakt === booking.Bokningsnummer
            );
            for (const child of children) {
              items.push({ type: 'booking', booking: child, isChild: true });
            }
          } else if (
            !bookings.some(b => b.Bokningsnummer === booking.Huvudakt)
          ) {
            items.push({ type: 'booking', booking, isChild: false });
          }
        }
      }
    });
    return items;
  }, [bookingDays, dayCount, viewStartTime]);

  const currentDateIndex = useMemo(() => {
    const targetDate = getDate(props.currentDate);
    const idx = flatItems.findIndex(
      item =>
        item.type === 'header' && item.date.getTime() >= targetDate.getTime()
    );
    return idx >= 0 ? idx : flatItems.length > 0 ? flatItems.length - 1 : 0;
  }, [flatItems, props.currentDate]);

  useEffect(() => {
    scrollToCurrentIndexRef.current = () => {
      listViewRef?.current?.scrollToIndex({
        index: currentDateIndex,
        align: 'start',
      });
    };
    return () => {
      scrollToCurrentIndexRef.current = null;
    };
  }, [currentDateIndex, listViewRef, scrollToCurrentIndexRef]);

  const onScrolledDateChange = props.onScrolledDateChange;

  const handleRangeChanged = useCallback(
    ({ startIndex }: { startIndex: number; endIndex: number }) => {
      if (!onScrolledDateChange) return;
      for (let i = startIndex; i >= 0; i--) {
        const item = flatItems[i];
        if (item?.type === 'header') {
          onScrolledDateChange(item.date);
          return;
        }
      }
    },
    [flatItems, onScrolledDateChange]
  );

  const allResIds = useMemo(
    () => props.resources.map(res => res.id),
    [props.resources]
  );

  const onSelectionChange = props.onSelectionChange;
  const onSelectionClick = props.onSelectionClick;
  const onBookingClick = props.onBookingClick;

  const handleDayClick = useCallback(
    (date: Date) => (event: React.MouseEvent) => {
      if (onSelectionChange && onSelectionClick) {
        onSelectionChange(undefined, undefined, undefined);
      }
      if (onSelectionClick) {
        onSelectionClick(event.target, date, undefined, allResIds);
      }
      event.stopPropagation();
      event.preventDefault();
    },
    [allResIds, onSelectionChange, onSelectionClick]
  );

  return (
    <EDBox sx={{ height: `${props.height}px`, width: `${props.width}px` }}>
      <EDList<ListItem>
        virtual
        totalCount={flatItems.length + (props.bottomSpacing ? 1 : 0)}
        initialTopMostItemIndex={currentDateIndex}
        onRangeChanged={handleRangeChanged}
        item={(index: number) => {
          if (index >= flatItems.length) {
            return <div style={{ height: props.bottomSpacing }} />;
          }
          const listItem = flatItems[index];
          return listItem.type === 'header' ? (
            <EDListItem>
              <MuiButtonBase onClick={handleDayClick(listItem.date)}>
                <DayCaptionTypography
                  className={classNames({
                    ['redDay']: listItem.redDay,
                    ['today']: listItem.today,
                  })}
                >
                  {dateCaption(listItem.date, false)}{' '}
                  {firstUpper(getDayNameText(listItem.date))}
                </DayCaptionTypography>
              </MuiButtonBase>
            </EDListItem>
          ) : (
            <BookingListItem disableVerticalPadding isChild={listItem.isChild}>
              <EDCalendarViewDayViewBooking
                booking={listItem.booking}
                onBookingClick={onBookingClick}
                boldTitle={true}
                showLocation={true}
                view="List"
              />
            </BookingListItem>
          );
        }}
        ref={listViewRef}
      >
        {null}
      </EDList>
    </EDBox>
  );
};

const BookingListItem = styled(EDListItem, {
  name: 'BookingListItem',
  shouldForwardProp: prop => prop !== 'isChild',
})<{ isChild: boolean }>(({ theme, isChild }) => ({
  ...(isChild && { paddingLeft: theme.spacing(3) }),
}));

const DayCaptionTypography = styled(EDTypography, {
  name: 'DayCaptionTypography',
})(({ theme }) => ({
  fontSize: theme.spacing(1.75),
  '&.redDay': {
    color: 'red',
  },
  '&.today': {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  whiteSpace: 'nowrap',
}));
